import { useState } from 'react';
import {
  RESOLUTION_LEVELS,
  SUBJECT_VOCABULARY_BY_VERTICAL,
  type BulkRowsSet,
  type DuplicateAnswer,
  type ImportCounts,
  type ImportRow,
  type ImportRowFilter,
  type ImportShape,
  type ResolutionLevel,
  type SetRowBody,
  type SubjectStatus,
} from '@ni/shared';
import { useOrg } from '../../api/app-context';
import type { ImportRowParams } from '../../api/client';
import { useBulkImportRows, useImportRows, useSetImportRow } from '../../api/hooks';
import { loadErrorLine } from '../../api/errors';
import {
  IMPORT_BULK_APPLY,
  IMPORT_BULK_APPLYING,
  IMPORT_BULK_DUPLICATE_DIFFERENT,
  IMPORT_BULK_DUPLICATE_SAME,
  IMPORT_BULK_STATUS_OFF,
  IMPORT_BULK_STATUS_OFF_SHORT,
  IMPORT_CONFLICT_MIXED,
  IMPORT_DUPLICATE_DIFFERENT,
  IMPORT_DUPLICATE_SAME,
  IMPORT_NOTES_RETRY_ELSEWHERE,
  IMPORT_PEOPLE_DIRECT_ONLY,
  IMPORT_ROWS_EMPTY,
  IMPORT_ROWS_SEARCH_PLACEHOLDER,
  IMPORT_ROW_NAME_SAVE,
  IMPORT_ROW_NAME_SAVING,
  IMPORT_ROW_NAME_KEEP,
  IMPORT_ROW_NAME_KEEP_HINT,
  IMPORT_ROW_OUTCOME_LABELS,
  IMPORT_TAB_NOT_YET,
  LOADING_MORE,
  LOAD_MORE,
  RESOLUTION_LEVEL_HINTS,
  RESOLUTION_LEVEL_LABELS,
  binReasonAlsoLine,
  binReasonLine,
  binReasonWantsAName,
  importConflictDropLine,
  importConflictLine,
  importDuplicateGroupLine,
  importFoldedIntoLine,
  importNeedsALookChip,
  importPeopleCount,
  importRowNotesLine,
  importRowNotesStatusLine,
  importRowsInAll,
  linkSkippedLine,
  previewOutcomeLine,
  rowEventOccasions,
  rowEventRoleLine,
  subjectStatusLabel,
} from '../../lib/copy';
import { formatCount, joinClauses, plural } from '../../lib/format';
import { SEARCH_DEBOUNCE_MS, useSettled } from '../../lib/debounce';
import { CollapsedBin } from '../../components/ui/collapsed-bin';
import { ErrorLine } from '../../components/ui/error-line';
import { WorkingButton } from '../../components/ui/working-button';
import { NameConflictCandidateCard } from '../../components/name-conflict/name-conflict';
import { DuplicateGroupCard, groupImportRows } from '../../components/imports/duplicate-group';
import { RowLevelSelect, RowStatusSelect } from '../../components/imports/row-controls';
import { useImportPage } from './session';
import { EventFold } from './event-fold';
import { Loading } from '../../components/ui/loading';
import { EmptyLine } from '../../components/ui/empty-line';

/**
 * **The People tab** (import note §1.3, §1.8) — every person the file produces,
 * with what they will be, what the dry run says will happen to them, and the one
 * gesture that settles a row.
 *
 * The bin is not a second list. "Needs a look" is a filter over the same rows,
 * and its chips are `counts.by_bin_reason` — because the number is one thing the
 * office acts on and several different questions underneath it: five bad names
 * and the two ends of an identifier collision are not one gesture.
 *
 * Everything the tab reads is `GET /rows`, filtered and keyset-paged. The
 * controls *are* the parameters *are* the query key, so a changed chip is a
 * different read that begins at page one and nothing has to remember to throw
 * the old pages away — the property the People screen's key has for the same
 * reason.
 *
 * **The controls are one stack of full-width rows** (the 2026-09-06 pass). They
 * were four blocks with four geometries — a search box, a flex row of segments,
 * a wrapping chip row, and two bars underneath with their own margins — so
 * nothing on the left lined up with anything under it and the two counts sat at
 * two different heights on two different sides. One column, one control height,
 * one rhythm, and every count on the right of the row it counts.
 */

export function ImportPeopleTab() {
  const { importId, session } = useImportPage();
  const org = useOrg();

  const [typed, setTyped] = useState('');
  const [q, search] = useSettled('', SEARCH_DEBOUNCE_MS);
  const [level, setLevel] = useState<ResolutionLevel | 'all'>('all');
  const [outcome, setOutcome] = useState<ImportRowParams['outcome'] | 'all'>('all');
  const [binReason, setBinReason] = useState<string | null>(null);
  const [inBin, setInBin] = useState(false);
  /* **Which row is being written, not whether any is** (D14). One boolean over
     the whole list disabled six hundred controls while one of them was in
     flight, and `aria-busy` on a container announces the whole list as busy. */
  const [writing, setWriting] = useState<string | null>(null);

  const params: ImportRowParams = {
    ...(q ? { q } : {}),
    ...(level === 'all' ? {} : { level }),
    ...(outcome === 'all' || outcome === undefined ? {} : { outcome }),
    ...(binReason ? { bin_reason: binReason as 'initials_only' } : {}),
    ...(inBin ? { bin: 'true' as const } : {}),
    /* **Always grouped** (§1.5): a name the file printed twice comes back with
       its copies together wherever the list is read from, because the row that
       asks "same person?" while the other copy is four hundred rows down is the
       thing this tab could not answer. It moves nothing else — a file with no
       repeats is in exactly the order it was. */
    group: 'duplicates' as const,
  };

  const rows = useImportRows(importId, params);
  const bulk = useBulkImportRows(importId);
  const setRow = useSetImportRow(importId);

  const entries = rows.data?.pages.flatMap((page) => page.rows) ?? [];
  /* One answer, whichever card it was pressed on: the row being written is named
     so the rest of the list stays live, which is the rule the row card already
     kept and the group card keeps for each of its copies. */
  const write = (locator: string, body: SetRowBody): void => {
    setWriting(locator);
    setRow.mutate({ locator, body }, { onSettled: () => setWriting(null) });
  };
  const total = rows.data?.pages[0]?.total ?? 0;
  const vocabulary = SUBJECT_VOCABULARY_BY_VERTICAL[org.vertical];
  const editable = session.state === 'in_progress' && !session.committed;

  if (session.plan === null) {
    return <EmptyLine>{IMPORT_TAB_NOT_YET.people}</EmptyLine>;
  }

  /* The filter the bulk edit reaches is the filter on screen, spelled the way
     the bulk body spells it — `bin` is a boolean there and a word on the URL. */
  const bulkFilter: ImportRowFilter = {
    ...(q ? { q } : {}),
    ...(level === 'all' ? {} : { level }),
    ...(outcome === 'all' || outcome === undefined ? {} : { outcome }),
    ...(binReason ? { bin_reason: binReason as 'initials_only' } : {}),
    ...(inBin ? { bin: true } : {}),
  };

  return (
    <div className="import-people">
      <div className="import-controls">
        <ImportRowControls
          typed={typed}
          level={level}
          outcome={outcome}
          binReason={binReason}
          inBin={inBin}
          counts={session.counts}
          onTyped={(value) => {
            setTyped(value);
            search(value);
          }}
          onLevel={setLevel}
          onOutcome={setOutcome}
          onBin={(reason) => {
            const next = binFilterAfter({ binReason, inBin }, reason);
            setBinReason(next.binReason);
            setInBin(next.inBin);
          }}
        />

        {/* The number moves when a chip is pressed and when a bulk edit lands,
            and a reader who cannot see it moving is left to guess whether the
            gesture reached anything. The bulk row carries it while the list is
            editable and the row is in view, so it stands on its own line
            otherwise. */}
        {!editable || session.target_event ? (
          <div className="control-line">
            <span className="tag-faint count" aria-live="polite">
              {importPeopleCount(total)}
            </span>
          </div>
        ) : null}

        {editable ? (
          <EventFold folded={session.target_event !== null}>
            <BulkBar
              statuses={vocabulary?.statuses ?? []}
              vertical={org.vertical}
              matched={total}
              /* **A status is a subject's and nobody else's** (§1.3, D8). Over a
               filter the only level this gesture can be sure of is the one it is
               setting, so the status control is offered when the list is narrowed
               to tracked people and otherwise says why — the server refuses the
               pair outright, and a control that could only be refused is worse
               than one that is off with a reason. */
              level={level}
              /* The two answers a repeat has are offered where the repeats are: the
               filter that holds both ends of every pair is the chip itself. */
              duplicates={binReason === 'duplicate_name'}
              working={bulk.isPending}
              error={bulk.error}
              onApply={(set) => bulk.mutate({ filter: bulkFilter, set })}
            />
          </EventFold>
        ) : null}
      </div>

      {/* **What this list is, and what it is not** (§1.11): the dry run's rows
          are the file's direct statements — a column, a heading, a date — and
          the bios are a second pass behind the people. The office read "Medici
          Day" under a name after a preview and asked where the rest of what the
          file said had gone; nothing on the page answered. It does now, under
          the counts, while the answer is still worth having. */}
      {session.previewed_at !== null && !session.committed ? (
        <div className="src">{IMPORT_PEOPLE_DIRECT_ONLY}</div>
      ) : null}

      {rows.isPending ? (
        <EmptyLine>
          <Loading />
        </EmptyLine>
      ) : entries.length === 0 ? (
        <EmptyLine>{rows.isError ? loadErrorLine(rows.error) : IMPORT_ROWS_EMPTY}</EmptyLine>
      ) : (
        <>
          <div className="import-rows">
            {/* **The copies of one name are one card** (§1.5): the server has put
                them next to each other and named the group on each, and this is
                where they stop being a row apiece asking the same question with
                no way to read the two together. */}
            {groupImportRows(entries).map((entry) =>
              entry.kind === 'group' ? (
                <DuplicateGroupCard
                  key={entry.key}
                  group={entry}
                  shape={session.shape}
                  vertical={org.vertical}
                  statuses={vocabulary?.statuses ?? []}
                  editable={editable}
                  writing={setRow.isPending ? writing : null}
                  onSet={(locator, body) => write(locator, body)}
                />
              ) : (
                <ImportRowCard
                  key={entry.row.locator}
                  row={entry.row}
                  index={entry.index}
                  shape={session.shape}
                  vertical={org.vertical}
                  statuses={vocabulary?.statuses ?? []}
                  editable={editable}
                  busy={setRow.isPending && writing === entry.row.locator}
                  onSet={(body) => write(entry.row.locator, body)}
                />
              ),
            )}
          </div>

          {rows.hasNextPage || rows.isError ? (
            <div className="list-foot">
              {rows.hasNextPage ? (
                <WorkingButton
                  label={LOAD_MORE}
                  workingLabel={LOADING_MORE}
                  working={rows.isFetchingNextPage}
                  variant="act"
                  tiny
                  onClick={() => void rows.fetchNextPage()}
                />
              ) : null}
              {rows.isError ? <ErrorLine error={rows.error} load /> : null}
            </div>
          ) : null}
        </>
      )}

      {setRow.isError ? <ErrorLine error={setRow.error} /> : null}
    </div>
  );
}

/** What the bin chips are showing: the whole bin, or one rule's rows. */
export interface BinFilter {
  readonly binReason: string | null;
  readonly inBin: boolean;
}

/**
 * **Pressing a chip narrows the bin or widens it; it never lies about what it
 * will show** (§1.8, D9).
 *
 * The bin is one number the office acts on and several different questions
 * underneath it, and the chips are the way between them: "Needs a look" is every
 * row still wanting an answer, a rule's chip is every row that rule caught.
 *
 * **Those are two different sets, and the chip carries the second number**, so a
 * rule's chip does not also ask for the bin: `by_bin_reason` counts every row a
 * rule ever caught, answered or not — on purpose, because the chip is the way
 * back to the rows one rule produced — and asking for `bin: true` beside it
 * showed nine rows under a chip reading twelve. Pressing it again goes back to
 * the whole bin, which is where the office was.
 *
 * Pure, and exported, because the rule is three cases and each of them is a way
 * the last one got it wrong.
 */
export function binFilterAfter(current: BinFilter, pressed: string | null): BinFilter {
  /* A rule's chip: that rule's rows, all of them — or off, back to the bin. */
  if (pressed !== null) {
    return current.binReason === pressed
      ? { binReason: null, inBin: true }
      : { binReason: pressed, inBin: false };
  }
  /* "Needs a look" with a rule showing: widen to the bin. */
  if (current.binReason !== null) return { binReason: null, inBin: true };
  /* "Needs a look" on its own: the one place it is a toggle. */
  return { binReason: null, inBin: !current.inBin };
}

/* ————————————————————————— the controls ————————————————————————— */

/** The outcome filter's words — every commit outcome, plus "all". */
const OUTCOME_FILTERS = ['pending', 'created', 'attached', 'skipped', 'failed'] as const;

export function ImportRowControls({
  typed,
  level,
  outcome,
  binReason,
  inBin,
  counts,
  onTyped,
  onLevel,
  onOutcome,
  onBin,
}: {
  typed: string;
  level: ResolutionLevel | 'all';
  outcome: ImportRowParams['outcome'] | 'all';
  binReason: string | null;
  inBin: boolean;
  counts: ImportCounts;
  onTyped: (value: string) => void;
  onLevel: (level: ResolutionLevel | 'all') => void;
  onOutcome: (outcome: ImportRowParams['outcome'] | 'all') => void;
  /** `null` toggles the whole bin; a rule narrows to that rule's rows. */
  onBin: (reason: string | null) => void;
}) {
  /* Only the rules that fired, which is what the counts carry: a clean file
     shows the bin chip and nothing under it, and a chip for a check nothing
     failed would be a filter that selects nothing by construction. */
  const reasons = Object.entries(counts.by_bin_reason).filter(([, count]) => (count ?? 0) > 0);
  const everyone = counts.by_level.subject + counts.by_level.known + counts.by_level.set_aside;

  /* Every outcome is `pending` until a commit has run, so the filter is not
     offered on a session that has not: five words, four of which select
     nothing, is a control that only makes the tab look busy. */
  const committed =
    counts.by_outcome.created +
      counts.by_outcome.attached +
      counts.by_outcome.skipped +
      counts.by_outcome.failed >
    0;

  return (
    <>
      <div className="control-line">
        <div className="field search-field">
          <label className="label" htmlFor="import-rows-q">
            Search
          </label>
          <input
            id="import-rows-q"
            type="search"
            autoComplete="off"
            placeholder={IMPORT_ROWS_SEARCH_PLACEHOLDER}
            value={typed}
            onChange={(event) => onTyped(event.target.value)}
          />
        </div>
      </div>

      <div className="control-line">
        <div className="control-side">
          <div className="segment" role="group" aria-label="Who to show">
            {(['all', ...RESOLUTION_LEVELS] as const).map((candidate) => (
              <button
                key={candidate}
                type="button"
                className={candidate === level ? 'seg-option on' : 'seg-option'}
                aria-pressed={candidate === level}
                onClick={() => onLevel(candidate)}
              >
                {candidate === 'all' ? 'All' : RESOLUTION_LEVEL_LABELS[candidate]}
              </button>
            ))}
          </div>

          {committed ? (
            <div className="segment" role="group" aria-label="What happened">
              {(['all', ...OUTCOME_FILTERS] as const).map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  className={candidate === outcome ? 'seg-option on' : 'seg-option'}
                  aria-pressed={candidate === outcome}
                  onClick={() => onOutcome(candidate)}
                >
                  {candidate === 'all' ? 'All' : IMPORT_ROW_OUTCOME_LABELS[candidate]}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* The size of the thing being filtered, on the line the filter is on. */}
        <span className="tag-faint count">{importRowsInAll(everyone)}</span>
      </div>

      {/* The hints the level buttons carried in a `title`, which is invisible to
          a keyboard and unreliable on a touch screen — and what they carry is
          the whole difference between the three levels. One muted line under the
          segments they are about, rather than a paragraph at the foot of the
          controls with two rows of other things in between. */}
      <div className="src control-note">
        {(['subject', 'known', 'set_aside'] as const)
          .map(
            (candidate) =>
              `${RESOLUTION_LEVEL_LABELS[candidate]}: ${RESOLUTION_LEVEL_HINTS[candidate]}`,
          )
          .join(' ')}
      </div>

      {/* **The chips are their own list and their own count** (D5). They used to
          be nested inside `needs_a_look > 0`, and `needs_a_look` counts only
          what is *unanswered* — so the moment the office finished settling the
          bin every chip disappeared, taking with it the only way back to the
          rows a rule caught. `by_bin_reason` counts every row the rule ever
          caught, answered or not, on purpose; the chips follow it, and so does
          the list a chip opens. */}
      {counts.needs_a_look > 0 || reasons.length > 0 ? (
        <div className="control-line">
          <div className="chips" role="group" aria-label="Needs review">
            <button
              type="button"
              className={inBin && binReason === null ? 'chip on' : 'chip'}
              aria-pressed={inBin && binReason === null}
              onClick={() => onBin(null)}
            >
              {importNeedsALookChip(counts.needs_a_look)}
            </button>
            {reasons.map(([rule, count]) => (
              <button
                key={rule}
                type="button"
                className={binReason === rule ? 'chip on' : 'chip'}
                aria-pressed={binReason === rule}
                onClick={() => onBin(rule)}
              >
                {binReasonLine(rule) ?? rule} · {formatCount(count ?? 0)}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </>
  );
}

/**
 * **One gesture across everything on screen** (§1.8). Deliberately narrower than
 * a row's own: a name and a namesake pick are answers about one person, and
 * setting either across a filter would be a way to get one person's answer onto
 * forty rows.
 *
 * The duplicate answer is the exception, and it is not one really: "these
 * repeats are repeats" is a statement about the *file*, which is exactly what
 * the office is looking at when the `duplicate_name` chip is the filter. The
 * server answers each same-name group whole, so neither end of a pair is left
 * holding a question.
 */
export function BulkBar({
  statuses,
  vertical,
  matched,
  level,
  duplicates = false,
  working,
  error,
  onApply,
}: {
  statuses: readonly SubjectStatus[];
  vertical: string;
  matched: number;
  /** What the list is narrowed to — a status only reaches tracked people. */
  level: ResolutionLevel | 'all';
  /** Whether the list on screen is the repeats, which is what has the answer. */
  duplicates?: boolean;
  working: boolean;
  error: unknown;
  onApply: (set: BulkRowsSet) => void;
}) {
  const [chosen, setChosen] = useState<ResolutionLevel | ''>('');
  const [status, setStatus] = useState<SubjectStatus | ''>('');

  /* Only a subject has a status (§1.3), and over a filter the only level this
     gesture can be sure of is the one it is setting or the one the list is
     narrowed to. Anything else and the server refuses the pair — so the control
     is off, with the reason on it, rather than offered and refused. */
  const statusReaches = chosen === 'subject' || (chosen === '' && level === 'subject');

  const set: BulkRowsSet = {
    ...(chosen === '' ? {} : { level: chosen }),
    ...(status === '' || !statusReaches ? {} : { status, level: 'subject' as const }),
  };
  const nothingChosen = chosen === '' && (status === '' || !statusReaches);

  return (
    <>
      <div className="control-line import-bulk">
        <div className="control-side">
          <select
            className="control"
            aria-label="Set Tracked or Known on everyone matching"
            value={chosen}
            onChange={(event) => setChosen(event.target.value as ResolutionLevel | '')}
          >
            <option value="">Leave as is</option>
            {RESOLUTION_LEVELS.map((candidate) => (
              <option key={candidate} value={candidate}>
                {RESOLUTION_LEVEL_LABELS[candidate]}
              </option>
            ))}
          </select>

          {statuses.length > 0 ? (
            <select
              className="control"
              aria-label="Set the status on everyone matching"
              aria-describedby={statusReaches ? undefined : 'import-bulk-status-hint'}
              /* The whole reason on the control itself, where the hand that
                 reaches for it is — and the clause under the row for the reader
                 who never reaches for it at all. */
              title={statusReaches ? undefined : IMPORT_BULK_STATUS_OFF}
              value={statusReaches ? status : ''}
              disabled={!statusReaches}
              onChange={(event) => setStatus(event.target.value as SubjectStatus | '')}
            >
              <option value="">Leave the status alone</option>
              {statuses.map((candidate) => (
                <option key={candidate} value={candidate}>
                  {subjectStatusLabel(vertical, candidate) ?? candidate}
                </option>
              ))}
            </select>
          ) : null}

          <WorkingButton
            label={IMPORT_BULK_APPLY}
            workingLabel={IMPORT_BULK_APPLYING}
            variant="act"
            tiny
            working={working}
            disabled={nothingChosen || matched === 0}
            onClick={() => onApply(set)}
          />

          {duplicates ? (
            <>
              <WorkingButton
                label={IMPORT_BULK_DUPLICATE_SAME}
                workingLabel={IMPORT_BULK_APPLYING}
                variant="act"
                tiny
                working={working}
                disabled={matched === 0}
                onClick={() => onApply({ duplicate: 'same' })}
              />
              <WorkingButton
                label={IMPORT_BULK_DUPLICATE_DIFFERENT}
                workingLabel={IMPORT_BULK_APPLYING}
                variant="act"
                tiny
                working={working}
                disabled={matched === 0}
                onClick={() => onApply({ duplicate: 'different' })}
              />
            </>
          ) : null}
        </div>

        <span className="tag-faint count" aria-live="polite">
          {importPeopleCount(matched)}
        </span>
      </div>

      {!statusReaches && statuses.length > 0 ? (
        <div className="src control-note" id="import-bulk-status-hint">
          {IMPORT_BULK_STATUS_OFF_SHORT}
        </div>
      ) : null}
      {error ? <ErrorLine error={error} /> : null}
    </>
  );
}

/* ————————————————————————— one row ————————————————————————— */

/** Above this many namesakes the cards are collapsed — the pick list's own line. */
const CANDIDATES_SHOWN = 5;

export function ImportRowCard({
  row,
  index,
  shape,
  vertical,
  statuses,
  editable,
  busy,
  onSet,
}: {
  row: ImportRow;
  /** Where this row sits in the list — the fallback name for a row with none. */
  index?: number;
  /**
   * What the parser made of the file — the word the prose line uses for a unit:
   * a **bio** out of a document, a **note** out of a grid.
   */
  shape: ImportShape;
  vertical: string;
  statuses: readonly SubjectStatus[];
  editable: boolean;
  busy: boolean;
  onSet: (body: {
    level?: ResolutionLevel;
    status?: SubjectStatus;
    name?: string;
    pick?: string;
    relation_picks?: Record<string, string>;
    duplicate?: DuplicateAnswer;
  }) => void;
}) {
  const [name, setName] = useState<string | null>(null);
  /* **What a control is about, said the way the office says it.** The labels
     were built from `row.locator`, which is the parser's own address —
     `contacts!A42`, `p3:b7` — and a screen reader announcing "The name on row
     contacts!A42" is reading out an internal key. The person's name is what the
     row is, and the position in the list is the fallback for a row whose name is
     the thing being fixed. */
  /* The locator is the parser's address and may hold anything — `contacts!A42`,
     `p3:b7` — so it is slugged before it becomes an `id` the `aria-describedby`
     points at. */
  const hintId = `import-level-${row.locator.replace(/[^a-zA-Z0-9_-]+/g, '-')}`;
  const who =
    row.display_name.trim().length > 0
      ? row.display_name
      : index === undefined
        ? 'this entry'
        : `entry ${index}`;

  const preview = row.preview;
  const reason = preview?.bin_reason ?? null;
  const candidates = preview?.candidates ?? [];
  const group = preview?.duplicate_group ?? null;
  /* The group carries this row too — it is the list the group card is drawn
     from — so the line that says who *else* is on the file takes it back out. */
  const peers = (group ?? []).filter((copy) => copy.locator !== row.locator);
  /* The name editor is offered on the eight shape checks and not on the two
     identity outcomes: re-stating the name a row already carries leaves the file
     saying exactly what it said (§1.8). */
  const fixable = editable && binReasonWantsAName(reason);
  /* A level settles a name flag as well as a fixed name does (`needsALook`), but
     the level control already shows the row's level, and choosing the value a
     select already has fires nothing. So keeping the name gets its own button,
     which answers with the level the row already has. Not on a set-aside row:
     that answer is "not this one", and the level select is where it changes. */
  const keepable =
    fixable && row.level !== 'set_aside' && !row.resolution?.level && !row.resolution?.name;
  const answered = row.resolution?.duplicate ?? null;
  /* Which person a fold landed on, named rather than addressed: the survivor is
     one of the rows the group already lists. */
  const foldedInto =
    preview?.outcome === 'folded' && preview.fold_target
      ? (group?.find((peer) => peer.locator === preview.fold_target)?.display_name ??
        /* A disagreement answered *same person* folds too, and the list its
           survivor is named on is the conflict's rather than the repeat's. */
        preview.conflict?.rows.find((one) => one.locator === preview.fold_target)?.display_name ??
        null)
      : null;
  /* **The disagreement this row is one end of**, wherever the row has one —
     including a row *filed* under a shape check that is also one end of it, which
     is a question the office could otherwise neither see nor answer (the reason
     is the head of the account, not the whole of it).

     A row the office has already answered still carries it: the identifiers have
     not changed, and the block is what tells the commit which of them to write to
     nobody. So the question stays on screen with the answer pressed, which is
     also how it is changed. */
  const conflict = preview?.conflict ?? null;
  const dropLine = conflict ? importConflictDropLine(conflict) : null;
  /* **Every row of this disagreement answered, and they do not agree** — the
     server's own reading of the set, because it is the only thing that holds it.
     A row cannot tell "nobody has got to the others" from "the others said
     something else", and the first draft guessed at it from this row's own chip
     plus a session-wide `preview_stale` — which hid a genuinely mixed set
     whenever anybody had answered anything else on the file. The chips keep
     showing *this row's* word, which is the one thing the office can change from
     here. */
  const mixedAnswers = conflict?.mixed ?? false;
  /* The two questions the same two words answer, and the only two that offer
     them: a name repeated, and two rows the file cannot tell apart. */
  const asksWhichPerson = reason === 'duplicate_name' || conflict !== null;
  const also = binReasonAlsoLine(preview?.bin_reasons ?? []);
  /* **Where this row's prose goes** (§1.11, §1.12). Before the commit it is what
     the row *will* file and what becomes of it; after one it is how far the
     reading has got on the person the row became, which is the same line
     answering the same question once there is an answer. The second replaces the
     first rather than sitting under it: "3 notes · read after import" is a plan,
     and a committed row is past plans. */
  const filing = preview ? importRowNotesLine(preview.notes, shape) : null;
  const readSoFar = row.notes_status === null ? null : importRowNotesStatusLine(row.notes_status);
  const notesLine = readSoFar ?? filing;

  return (
    /* Busy is this row's, not the list's: `aria-busy` on a container announces
       every row on the page as busy, and one boolean over the whole list
       disabled six hundred controls while one of them was in flight. */
    <div className="import-row" aria-busy={busy}>
      <div className="import-row-head">
        <div className="import-row-name">
          {fixable ? (
            <>
              <input
                className="control"
                type="text"
                aria-label={`The name on ${who}`}
                value={name ?? row.display_name}
                disabled={busy}
                onChange={(event) => setName(event.target.value)}
              />
              {name !== null && name.trim().length > 0 && name.trim() !== row.display_name ? (
                <WorkingButton
                  label={IMPORT_ROW_NAME_SAVE}
                  workingLabel={IMPORT_ROW_NAME_SAVING}
                  variant="act"
                  tiny
                  working={busy}
                  onClick={() => {
                    onSet({ name: name.trim() });
                    setName(null);
                  }}
                />
              ) : keepable ? (
                <WorkingButton
                  label={IMPORT_ROW_NAME_KEEP}
                  workingLabel={IMPORT_ROW_NAME_SAVING}
                  title={IMPORT_ROW_NAME_KEEP_HINT}
                  variant="act"
                  tiny
                  working={busy}
                  onClick={() => onSet({ level: row.level })}
                />
              ) : null}
            </>
          ) : (
            row.display_name
          )}
        </div>

        {/* The two controls a row is settled with, drawn from the piece the
            group card's copies draw them from — one gesture, one control. */}
        <div className="import-row-controls">
          <RowLevelSelect
            who={who}
            level={row.level}
            describedBy={hintId}
            disabled={!editable || busy}
            onLevel={(level) => onSet({ level })}
          />

          {/* **Only a subject has a status** (§1.3), and the control follows the
              rule rather than the row's *stored* level: `row.level` is already
              the office's answer where it gave one, so a row just set to Known
              loses its status control on the next read, which is what the server
              means by the rule. */}
          {row.level === 'subject' ? (
            <RowStatusSelect
              who={who}
              status={row.status}
              vertical={vertical}
              statuses={statuses}
              disabled={!editable || busy}
              onStatus={(status) => onSet({ status, level: 'subject' })}
            />
          ) : null}
        </div>
      </div>

      <div className="import-row-lines">
        <div className="src" id={hintId}>
          {RESOLUTION_LEVEL_HINTS[row.level]}
        </div>
        {preview ? (
          <div className="src">
            {foldedInto
              ? importFoldedIntoLine(foldedInto)
              : previewOutcomeLine(preview.outcome, candidates.length)}
          </div>
        ) : null}

        {/* **What this person was to the file, on a file of occasions** — "host ·
            12 occasions", "guest of Ada Fenwick · 2 occasions". The count is of
            the whole person and not of this row: the rows that folded onto this
            one are the same member on another evening, and a line saying "1"
            beside a member who hosted twelve times would be counting the row the
            page happens to be drawing. Absent on every other file. */}
        {preview?.row_event ? (
          <div className="src">
            {joinClauses([
              rowEventRoleLine(preview.row_event.role, preview.row_event.host),
              rowEventOccasions(preview.row_event.occasions),
            ])}
          </div>
        ) : null}

        {/* The bio, and what happens to it. A row whose only line was "Medici
            Day" is a row whose whole paragraph is sitting unread with nothing on
            the page saying so — and once the reading has run, one of these units
            failing is a thing the office can act on, on the tab that has the
            gesture. */}
        {notesLine ? (
          <div className="src">
            {notesLine}
            {(row.notes_status?.failed ?? 0) > 0 ? ` · ${IMPORT_NOTES_RETRY_ELSEWHERE}` : ''}
          </div>
        ) : null}

        {/* **The bin line, and the gestures that answer it**, together: the
            reason, the rest of the account under it, who else on the file this
            row is being asked about, and the two answers to that question. */}
        {reason ? (
          <div className="import-row-bin">
            <div className="tag-faint amber-ink block">{binReasonLine(reason)}</div>
            {also ? <div className="src">{also}</div> : null}
            {reason === 'duplicate_name' && peers.length > 0 ? (
              <div className="src">{importDuplicateGroupLine(peers)}</div>
            ) : null}
            {/* **Which rows disagree with this one, and about what** (§1.5). The
                reason above says two rows share an identifier and disagree on
                another and names neither half of that; this is the half the dry
                run already knew, and under it what the second answer costs,
                because "the number is written to neither" is the part of it
                nobody would guess. */}
            {conflict ? (
              <>
                <div className="src">{importConflictLine(conflict, row.locator)}</div>
                {dropLine ? <div className="src">{dropLine}</div> : null}
                {/* **A pressed chip on a row that is still in the bin**, which is
                    what a set that does not agree with itself looks like from
                    one end of it. Nothing on the row says so otherwise. */}
                {mixedAnswers ? <div className="src">{IMPORT_CONFLICT_MIXED}</div> : null}
              </>
            ) : null}
            {asksWhichPerson && editable ? (
              <div className="chips" role="group" aria-label={`Is ${who} the other row too?`}>
                {(
                  [
                    ['same', IMPORT_DUPLICATE_SAME],
                    ['different', IMPORT_DUPLICATE_DIFFERENT],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={answered === value ? 'chip on' : 'chip'}
                    aria-pressed={answered === value}
                    disabled={busy}
                    onClick={() => onSet({ duplicate: value })}
                  >
                    {label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {row.outcome !== 'pending' ? (
          <div className="tag-faint block">{IMPORT_ROW_OUTCOME_LABELS[row.outcome]}</div>
        ) : null}

        {row.error ? <div className="src">{row.error}</div> : null}

        {row.link_skipped ? <div className="src">{linkSkippedLine(row.link_skipped)}</div> : null}

        {preview?.proposed && preview.proposed.length > 0 ? (
          <CollapsedBin
            label={`What it would write (${preview.proposed.length})`}
            openLabel="Hide what it would write"
          >
            {preview.proposed.map((line, index) => (
              <div className="bin-row" key={`${row.locator}-proposed-${index}`}>
                <div className="bname">{line}</div>
              </div>
            ))}
          </CollapsedBin>
        ) : null}

        {preview?.attachments && preview.attachments.length > 0 ? (
          <CollapsedBin
            label={`What it would add to their record (${preview.attachments.length})`}
            openLabel="Hide what it would add"
          >
            {preview.attachments.map((line, index) => (
              <div className="bin-row" key={`${row.locator}-attach-${index}`}>
                <div className="bname">{line}</div>
              </div>
            ))}
          </CollapsedBin>
        ) : null}
      </div>

      {preview?.identity_conflict ? (
        <p role="alert">
          Identifiers belong to different people. Correct the identifiers in the plan and preview
          again.
        </p>
      ) : null}
      {preview?.relationships?.map((relation) => (
        <label key={relation.key}>
          Who is {relation.target_name} in {who}’s relationship?
          <select
            className="control"
            disabled={!editable || busy}
            value={
              relation.candidates.some(
                (candidate) => candidate.locator === row.resolution?.relation_picks?.[relation.key],
              )
                ? row.resolution!.relation_picks![relation.key]
                : (relation.selected ?? '')
            }
            onChange={(event) => {
              const picks = { ...row.resolution?.relation_picks };
              if (event.target.value) picks[relation.key] = event.target.value;
              else delete picks[relation.key];
              onSet({ relation_picks: picks });
            }}
          >
            <option value="">Choose a person, then preview again</option>
            {relation.candidates.map((candidate) => (
              <option key={candidate.locator} value={candidate.locator}>
                {candidate.display_name} · {candidate.source_label} ·{' '}
                {candidate.identifiers.join(', ')}
              </option>
            ))}
          </select>
        </label>
      ))}
      {candidates.length > 0 ? (
        candidates.length <= CANDIDATES_SHOWN ? (
          <div className="candidates">
            {candidates.map((candidate) => (
              <NameConflictCandidateCard
                key={candidate.entity_id}
                candidate={candidate}
                working={busy}
                disabled={!editable || busy}
                label="This is them"
                workingLabel="Choosing…"
                subjectsPickable
                onUse={() => onSet({ pick: candidate.entity_id })}
              />
            ))}
          </div>
        ) : (
          /* Past five the cards are a wall rather than a question — the same
             line the held list draws, and for the same reason. */
          <CollapsedBin
            label={`${plural(candidates.length, 'person shares', 'people share')} this name`}
            openLabel="Hide the people who share this name"
          >
            {candidates.map((candidate) => (
              <div className="bin-row" key={candidate.entity_id}>
                <div className="bname">{candidate.display_name}</div>
                <div className="bdetail">
                  {plural(candidate.fact_count, 'record', 'records')}
                  {candidate.relationships.length > 0
                    ? ` · ${candidate.relationships.join(' · ')}`
                    : ''}
                </div>
                <div className="layer-actions">
                  <WorkingButton
                    label="This is them"
                    workingLabel="Choosing…"
                    variant="act"
                    tiny
                    working={busy}
                    disabled={!editable || busy}
                    onClick={() => onSet({ pick: candidate.entity_id })}
                  />
                </div>
              </div>
            ))}
          </CollapsedBin>
        )
      ) : null}
    </div>
  );
}
